import { CurrencyPipe, formatDate, formatNumber } from '@angular/common';
import { CmpsScorecardSubHeaderData } from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { HeaderRowData } from '../card--my-coaching-card/models/header-row-data.model';
import { I18nService } from '../i18n/i18n.service';
import { OverallScoreGridOptions } from '../scorecard/core/overall-score-grid-options.model';
import { WidgetType } from '../scorecard/core/template-definition.model';
import { TrendType } from '../scorecard/core/trend-type.model';
import { DescriptionType } from '../scorecard/scorecard-data/description-type.model';
import { Scorecard } from '../scorecard/scorecard-next.model';
import { PeriodTypeShallow, ScorecardDto } from '../scorecard/scorecard.model';
import { epicDependencies } from '../store';
import { User } from '../user/user.model';

import { defaultColor } from './constant/sc-category-color-constant';
import { getTrendData } from './general/get-trend-data.function';
import { TranslationKeyMap } from './my-scorecard.i18n';
import { ScorecardData } from './my-scorecard.model';

export function getScoreData(scorecard: Scorecard, user: User): ScorecardData {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    const i18nService = epicDependencies.injector.get<I18nService>(I18nService);
    const userLocale = i18nService.locale;
    const sc: ScorecardData = {};
    sc.headerRowData = getHeaderRowData(scorecard.header, user, userLocale);
    sc.scorecardSubHeader = {
        supergroupLabel: translateService.instant(
            TranslationKeyMap._804_SUPERGROUP_,
        ),
        scorecardId: translateService.instant(
            TranslationKeyMap._9947_SCORECARD_ID_,
        ),
        supergroupValue: scorecard.header.groupName,
        scorecardValue: scorecard.header.scorecardId.toString(),
    };

    const adjustedSc = scorecard.data.overallScores?.find(
        (e) => e.descriptionTypeId === DescriptionType.ADJUSTED_SCORE,
    );
    const overallScores = scorecard.data.overallScores?.find(
        (e) => e.descriptionTypeId === DescriptionType.OVERALL_SCORE,
    );
    const adjustedScore = adjustedSc != null ? adjustedSc : overallScores;
    const originalScore = scorecard.data.overallScores?.find(
        (e) => e.descriptionTypeId === DescriptionType.ORIGINAL_SCORE,
    );

    sc.adjustScoreData = {
        scoreLabel: translateService.instant(
            TranslationKeyMap._9361_ADJUSTED_SCORE_,
        ),
        scoreValue:
            adjustedScore?.scoreValue == null
                ? '-'
                : formatNumber(adjustedScore.scoreValue, userLocale, '1.0-2'),
    };

    const trend =
        (originalScore?.scoreValue != null ||
            scorecard.header.prevAdjustedPoints) ===
        scorecard.header.adjustedPoints
            ? TrendType.NEUTRAL
            : scorecard.header.trendId;

    if (originalScore != null) {
        sc.originalScoreData = {
            scoreLabel: translateService.instant(
                TranslationKeyMap._9360_ORIGINAL_SCORE_,
            ),
            scoreValue: formatNumber(
                originalScore.scoreValue,
                userLocale,
                '1.0-2',
            ),
        };
    } else {
        sc.adjustScoreData.scoreLabel = translateService.instant(
            TranslationKeyMap._9856_SCORECARD_SCORE,
        );
    }

    sc.trendData = getTrendData(
        adjustedScore,
        scorecard.header.prevAdjustedPoints,
        trend,
    );

    const rankWidget =
        scorecard?.data?.widgetTemplateConfig != null
            ? scorecard.data.widgetTemplateConfig.widgets.find(
                  (e) => e.widgetType === WidgetType.RANKINGS_GRID && e.enabled,
              )
            : null;
    const bonusWidget =
        scorecard?.data?.widgetTemplateConfig != null
            ? scorecard.data.widgetTemplateConfig.widgets.find(
                  (e) =>
                      e.widgetType === WidgetType.COMPENSATION_BONUS &&
                      e.enabled,
              )
            : null;
    const scCategory =
        scorecard?.data?.widgetTemplateConfig != null
            ? scorecard.data.widgetTemplateConfig.widgets.find(
                  (e) =>
                      e.widgetType === WidgetType.OVERALL_SCORE_GRID &&
                      e.enabled,
              )
            : null;
    const scOsConfigOptions = scorecard?.data?.templateConfig?.widgets.find(
        (e) => e.widgetType === WidgetType.OVERALL_SCORE_GRID,
    ).options as OverallScoreGridOptions;

    if (rankWidget != null) {
        sc.rankData = {
            titleLabel: translateService.instant(
                TranslationKeyMap._11185_RANKING,
            ),
            data: scorecard.data?.ranks?.map((e) => {
                return {
                    titleName: e.rankName != null ? e.rankName : '',
                    rankResult: e.rankResultScorecardPeriod,
                    totalPositions: e.totalPositionsScorecardPeriod,
                };
            }),
        };
    }

    if (bonusWidget != null) {
        sc.bonusData = {
            bonusLabel: translateService.instant(
                TranslationKeyMap._9308_ESTIMATED_PAYOUT,
            ),
            value: new CurrencyPipe(userLocale).transform(
                scorecard.data.compensationBonus.bonusAmount,
                'USD',
                'symbol-narrow',
                '1.2-2',
                userLocale,
            ),
        };
    }

    if (scCategory != null && scOsConfigOptions?.showCategory === true) {
        sc.scCategory = {
            color: scorecard.header.performanceClass?.colour ?? defaultColor,
            value:
                scorecard.header.performanceClass
                    ?.scorecardPerformanceClassName ??
                translateService.instant(TranslationKeyMap._9402_NA),
            label: translateService.instant(
                TranslationKeyMap._9880_MI2_SCORECARD_CATEGORY,
            ),
        };
    }
    return sc;
}
export function getHeaderRowData(
    scorecardData: ScorecardDto,
    user: User,
    userLocale: string,
): HeaderRowData {
    const formatWithUTC = (dateInput: Date | string): string => {
        let date: Date;
        if (typeof dateInput === 'string') {
            if (dateInput.includes('T')) {
                date = new Date(dateInput);
            } else {
                const parts = dateInput
                    .split('-')
                    .map((part) => parseInt(part, 10));
                date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
            }
        } else {
            date = new Date(
                Date.UTC(
                    dateInput.getFullYear(),
                    dateInput.getMonth(),
                    dateInput.getDate(),
                ),
            );
        }
        return formatDate(date, 'MMMM d, yyyy', userLocale, 'UTC');
    };

    const startDateFormatted =
        scorecardData.startDate != null
            ? formatWithUTC(scorecardData.startDate)
            : '';
    const endDateFormatted =
        scorecardData.endDate != null
            ? formatWithUTC(scorecardData.endDate)
            : '';

    const periodDate = `${startDateFormatted} - ${endDateFormatted}`;

    return {
        titleLabel: `${user.firstName} ${user.lastName}`,
        periodDate,
        hasHeaderIcon: true,
    };
}

export function getPeriodTypes(
    periodTypes: PeriodTypeShallow[],
): CmpsScorecardSubHeaderData[] {
    const periodTypesData: CmpsScorecardSubHeaderData[] = [];
    periodTypes = sort(periodTypes, 'periodTypeOrder');
    periodTypes.forEach((e) => {
        periodTypesData.push({
            value: e.periodTypeName,
            id: e.periodTypeId,
        });
    });
    return periodTypesData;
}

export function sort<T>(arr: T[], key: string): T[] {
    const ret = arr != null ? [...arr] : [];
    ret.sort((a, b) => a[key] - b[key]);
    return ret;
}
